import { useState, useEffect } from "react";

export interface DeviceFingerprint {
  fingerprint_hash: string;
  browser: string;
  os: string;
  platform: string;
  screen_resolution: string;
  timezone: string;
  language: string;
  device_info: {
    userAgent: string;
    vendor: string;
    platform: string;
    hardwareConcurrency: number;
    deviceMemory?: number;
    colorDepth: number;
    pixelRatio: number;
  };
}

export function useDeviceFingerprint() {
  const [fingerprint, setFingerprint] = useState<DeviceFingerprint | null>(() => {
    // Check sessionStorage for cached fingerprint
    const cached = sessionStorage.getItem("device_fingerprint");
    if (cached) {
      try {
        return JSON.parse(cached);
      } catch {
        return null;
      }
    }
    return null;
  });

  useEffect(() => {
    // Skip if already have cached fingerprint
    if (fingerprint) return;

    const generateFingerprint = () => {
      const nav = navigator as any;
      const screen = window.screen;
      
      // Detect browser
      const userAgent = nav.userAgent;
      let browser = "Unknown";
      if (userAgent.includes("Firefox")) browser = "Firefox";
      else if (userAgent.includes("Chrome")) browser = "Chrome";
      else if (userAgent.includes("Safari")) browser = "Safari";
      else if (userAgent.includes("Edge")) browser = "Edge";
      
      // Detect OS
      let os = "Unknown";
      if (userAgent.includes("Windows")) os = "Windows";
      else if (userAgent.includes("Mac")) os = "macOS";
      else if (userAgent.includes("Linux")) os = "Linux";
      else if (userAgent.includes("Android")) os = "Android";
      else if (userAgent.includes("iOS")) os = "iOS";
      
      const deviceInfo = {
        userAgent,
        vendor: nav.vendor || "Unknown",
        platform: nav.platform || "Unknown",
        hardwareConcurrency: nav.hardwareConcurrency || 0,
        deviceMemory: nav.deviceMemory,
        colorDepth: screen.colorDepth,
        pixelRatio: window.devicePixelRatio,
      };
      
      // Generate a simple hash from device characteristics
      const fingerprintString = JSON.stringify(deviceInfo);
      const hash = Array.from(fingerprintString)
        .reduce((hash, char) => {
          return ((hash << 5) - hash) + char.charCodeAt(0);
        }, 0)
        .toString(16);
      
      const fpData = {
        fingerprint_hash: hash,
        browser,
        os,
        platform: nav.platform || "Unknown",
        screen_resolution: `${screen.width}x${screen.height}`,
        timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
        language: nav.language || "en",
        device_info: deviceInfo,
      };

      // Cache in sessionStorage
      sessionStorage.setItem("device_fingerprint", JSON.stringify(fpData));
      setFingerprint(fpData);
    };
    
    generateFingerprint();
  }, [fingerprint]);

  return fingerprint;
}
